import type { FieldProps, RJSFSchema } from '@rjsf/utils';
import { Expand } from 'lucide-react';

import { Button } from '@/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/shadcn/dialog';
import { cn } from '@/shadcn/lib/utils';

import type { FormWriterContext } from '@/components/forms/components/form/writer/index';
import type { TableEditorConfiguration } from '@/components/ui/dataEditor/form/tableEditor.form.type';
import Table from '@/components/ui/table';
import type { TableRow } from '@/components/ui/table/table.type';
import { getByPath } from '@/utils';

type CustomFieldProps = FieldProps<TableRow[], RJSFSchema, FormWriterContext>;

type Props = CustomFieldProps & {
  uiSchema: Omit<CustomFieldProps['uiSchema'], 'ui:options'> & {
    'ui:options': {
      tableConfig: TableEditorConfiguration;
    };
  };
};

export const TableEditorFieldReader = (props: Props) => {
  const config = props.uiSchema?.['ui:options']?.tableConfig;

  if (!config) {
    return <ErrorMessage message="Table editor config is missing" />;
  }

  if (!props.formData) {
    return <ErrorMessage message="Table data is missing" />;
  }

  const { title, description } = props.schema;

  const { table: tableConfig, selectionOutputKey } = config;

  const selectorPath = tableConfig.dataSelectorPath ?? 'rowId';
  const initialSelectedRows = selectionOutputKey
    ? props.formData
        .filter(row => row[selectionOutputKey])
        .map(row => String(getByPath(row, selectorPath)))
    : undefined;

  return (
    <section className="flex size-full flex-col">
      <header className="flex w-full items-end justify-between pb-2 text-sm">
        <section className="flex flex-col">
          <h2
            className={cn(
              'font-semibold text-base',
              props.required &&
                'after:ml-1 after:text-destructive after:text-sm after:content-["*"]'
            )}
          >
            {title}
          </h2>
          {description && (
            <p className="text-neutral-500 text-xs">{description}</p>
          )}
        </section>
        <section className="flex items-center gap-2">
          <Dialog>
            <DialogTrigger asChild>
              <Button
                className="-mb-1"
                size="icon"
                type="button"
                variant="ghost"
              >
                <Expand />
              </Button>
            </DialogTrigger>
            <DialogContent className="h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-none">
              <DialogHeader>
                <DialogTitle>{title}</DialogTitle>
              </DialogHeader>
              <Table
                config={tableConfig}
                data={props.formData}
                update={
                  initialSelectedRows
                    ? { initialSelectedRows, selectionReadOnly: true }
                    : undefined
                }
              />
            </DialogContent>
          </Dialog>
        </section>
      </header>
      <div className="h-96 w-full" id={props.id}>
        <div className="size-full overflow-hidden rounded-md border border-input">
          <Table
            className="border-0"
            config={tableConfig}
            data={props.formData}
            update={
              initialSelectedRows
                ? { initialSelectedRows, selectionReadOnly: true }
                : undefined
            }
          />
        </div>
      </div>
    </section>
  );
};

const ErrorMessage = ({ message }: { message: string }) => (
  <div className="text-destructive">{message}</div>
);
